import { CredentialController } from '@/components/forms/FormController'
import ErrorMessage from '@/components/utils/ErrorMessage'
import { AuthResult, SignInStep } from '@/context/AuthContext'
import { zodResolver } from '@/lib/zod'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useCountdown } from 'usehooks-ts'
import { z } from 'zod'

import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@droidmap/frontend-ui'
import { Button } from '@droidmap/frontend-ui'

interface SendOTPFormProps {
  onRequestOTP: (email: string) => Promise<AuthResult>
  onVerifyOTP: (code: string) => Promise<{ success: boolean; message?: string }>
}

const emailCodeSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

type EmailCodeFormValues = z.infer<typeof emailCodeSchema>

const CODE_LENGTH = 8
const RESEND_COUNTDOWN_SECONDS = 60

export default function SendOTPForm({
  onRequestOTP,
  onVerifyOTP,
}: SendOTPFormProps) {
  const navigate = useNavigate()
  const [error, setError] = useState<string | undefined>()
  const [loading, setLoading] = useState(false)
  const [showCodeVerification, setShowCodeVerification] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const [otpValue, setOtpValue] = useState('')
  const [isResending, setIsResending] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const lastAttemptedCodeRef = useRef<string>('')
  const otpInputRef = useRef<HTMLInputElement>(null)

  // Countdown timer for resend button
  const [count, { startCountdown, resetCountdown }] = useCountdown({
    countStart: RESEND_COUNTDOWN_SECONDS,
    intervalMs: 1000,
    isIncrement: false,
    countStop: 0,
  })

  const isCountingDown = count > 0
  const isResendDisabled = isCountingDown || isResending || isVerifying

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailCodeFormValues>({
    resolver: zodResolver(emailCodeSchema),
    defaultValues: { email: '' },
    mode: 'onBlur',
  })

  // Auto-verify when the code is complete.
  //
  // This hangs off the edit itself rather than off an effect watching `otpValue`.
  // Typing the last digit *is* the event that should trigger verification, and
  // clearing the error *is* part of editing — expressing that directly removes
  // the effect's synchronous setState (the cascading render the compiler flags)
  // and the render in which a stale error was still shown after the user had
  // already started correcting the code. Nothing else ever sets a complete code:
  // every other write to `otpValue` clears it.
  const handleOtpChange = async (value: string) => {
    setOtpValue(value)

    // Clear error and reset ref when user modifies the code
    if (value.length < CODE_LENGTH) {
      setError('')
      lastAttemptedCodeRef.current = ''
      return
    }

    if (value === lastAttemptedCodeRef.current) {
      return
    }

    lastAttemptedCodeRef.current = value
    setIsVerifying(true)
    setError('')

    try {
      const result = await onVerifyOTP(value)
      if (!result.success) {
        setError(result.message || 'Invalid or expired code')
        // Keep the failed code in ref - user must modify to retry
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
      // Keep the failed code in ref - user must modify to retry
    } finally {
      setIsVerifying(false)
    }
  }

  // Refocus input after verification fails
  useEffect(() => {
    if (error && !isVerifying && otpInputRef.current) {
      otpInputRef.current.focus()
    }
  }, [error, isVerifying])

  // An unconfirmed user's email doesn't resolve to their Cognito account (the
  // email alias needs `email_verified=true`), so asking for a code fails as
  // "User does not exist." rather than telling them the account merely needs
  // confirming. The auth layer recovers the real username; send them to the
  // confirm screen with it prefilled so that screen doesn't repeat the lookup.
  // See #2422.
  const redirectIfUnconfirmed = useCallback(
    (result: AuthResult, email: string): boolean => {
      if (result.nextStep !== SignInStep.CONFIRM_SIGN_UP) {
        return false
      }

      navigate({
        to: '/confirm-signup',
        search: { username: result.resolvedUsername ?? email },
      })
      return true
    },
    [navigate],
  )

  const onSubmit = useCallback(
    async (data: EmailCodeFormValues) => {
      setError('')
      setLoading(true)

      try {
        const result = await onRequestOTP(data.email)
        if (result.success) {
          setUserEmail(data.email)
          setOtpValue('')
          lastAttemptedCodeRef.current = ''
          setShowCodeVerification(true)
          startCountdown()
        } else if (!redirectIfUnconfirmed(result, data.email)) {
          setError(result.message || 'Failed to send code')
        }
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err.message : 'An unknown error occurred',
        )
      } finally {
        setLoading(false)
      }
    },
    [onRequestOTP, redirectIfUnconfirmed, startCountdown],
  )

  const handleResendCode = useCallback(async () => {
    if (!userEmail || isResending) return

    setIsResending(true)
    setOtpValue('')
    setError('')

    try {
      const result = await onRequestOTP(userEmail)
      if (result.success) {
        lastAttemptedCodeRef.current = ''
        resetCountdown()
        startCountdown()
      } else if (!redirectIfUnconfirmed(result, userEmail)) {
        setError(result.message || 'Failed to resend code')
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setIsResending(false)
    }
  }, [
    onRequestOTP,
    redirectIfUnconfirmed,
    userEmail,
    isResending,
    startCountdown,
    resetCountdown,
  ])

  const handleBackToEmail = useCallback(() => {
    setShowCodeVerification(false)
    setUserEmail('')
    setOtpValue('')
    setError('')
    lastAttemptedCodeRef.current = ''
    resetCountdown()
  }, [resetCountdown])

  if (showCodeVerification) {
    return (
      <div className="space-y-6 animate-in fade-in-50 slide-in-from-bottom-4 duration-500">
        {/* Header Section */}
        <div className="space-y-4">
          <p>We've sent an 8-character verification code to:</p>

          <div className="bg-panel border border-border rounded-lg p-4 w-full max-w-sm mx-auto">
            <div className="text-micro text-text-dim mb-1">Email Address</div>
            <div className="text-base font-semibold text-text">{userEmail}</div>
          </div>

          <p className="text-sm text-center">
            The code expires shortly, so please enter it soon.
          </p>
        </div>

        {/* Code Input Section */}
        <div className="space-y-3">
          <div className="flex justify-center font-semibold text-accent text-lg w-full max-w-sm mx-auto">
            <InputOTP
              ref={otpInputRef}
              maxLength={CODE_LENGTH}
              value={otpValue}
              onChange={(value) => void handleOtpChange(value)}
              pattern="[0-9]*"
              disabled={isVerifying}
              className="gap-1 w-full justify-center"
            >
              <InputOTPGroup className="gap-2">
                <InputOTPSlot
                  index={0}
                  className="bg-bg-2 w-9 h-9 text-center border border-border-strong rounded"
                />
                <InputOTPSlot
                  index={1}
                  className="bg-bg-2 w-9 h-9 text-center border border-border-strong rounded"
                />
                <InputOTPSlot
                  index={2}
                  className="bg-bg-2 w-9 h-9 text-center border border-border-strong rounded"
                />
                <InputOTPSlot
                  index={3}
                  className="bg-bg-2 w-9 h-9 text-center border border-border-strong rounded"
                />
              </InputOTPGroup>
              <InputOTPSeparator className="hidden sm:block mx-1" />
              <InputOTPGroup className="gap-2">
                <InputOTPSlot
                  index={4}
                  className="bg-bg-2 w-9 h-9 text-center border border-border-strong rounded"
                />
                <InputOTPSlot
                  index={5}
                  className="bg-bg-2 w-9 h-9 text-center border border-border-strong rounded"
                />
                <InputOTPSlot
                  index={6}
                  className="bg-bg-2 w-9 h-9 text-center border border-border-strong rounded"
                />
                <InputOTPSlot
                  index={7}
                  className="bg-bg-2 w-9 h-9 text-center border border-border-strong rounded"
                />
              </InputOTPGroup>
            </InputOTP>
          </div>

          {/* Status messages */}
          <div className="text-center min-h-[32px]">
            {isVerifying && <p className="text-sm">Verifying code...</p>}
            {error && !isVerifying && <ErrorMessage content={error} />}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 w-full max-w-sm mx-auto">
          <Button
            onClick={handleResendCode}
            isLoading={isResending}
            disabled={isResendDisabled}
            className="flex-1"
          >
            {isResending ? (
              'Sending…'
            ) : isCountingDown ? (
              <>
                Resend code (
                <span className="font-mono">
                  {count.toString().padStart(2, '0')}s
                </span>
                )
              </>
            ) : (
              'Resend code'
            )}
          </Button>
          <Button
            type="button"
            onClick={handleBackToEmail}
            variant="secondary"
            className="flex-1"
            disabled={isVerifying}
          >
            Back
          </Button>
        </div>
      </div>
    )
  }

  return (
    // `onSubmit` writes `lastAttemptedCodeRef`, so building the submit handler
    // during render hands a ref-touching function to react-hook-form before
    // render is complete. Building it inside the submit event instead means the
    // ref is only ever reached from an event handler, where it is safe.
    <form
      onSubmit={(event) => void handleSubmit(onSubmit)(event)}
      className="space-y-6"
    >
      <CredentialController
        name="email"
        control={control}
        label="Email address"
        errors={errors}
        type="email"
        autoComplete="email"
      />
      <p className="text-sm">
        Enter your email address and we'll send you a verification code.
      </p>
      {error && <ErrorMessage content={error} />}
      <Button type="submit" className="w-full" isLoading={loading}>
        Email one-time code
      </Button>
    </form>
  )
}
